import axios from 'axios'
import { attachAuthInterceptors } from '@adthlete/shared-frontend'
import type { BidStatus } from '../types'

const athleteApi = axios.create({
  baseURL: '/api',
})

// Inject the in-memory access token and, on a 401 from a protected endpoint, refresh once
// and retry. Refresh failure clears user state via the onAuthCleared hook wired in main.tsx,
// so ProtectedRoute redirects to /signin.
attachAuthInterceptors(athleteApi, { appId: 'athlete' })

export default athleteApi

export const updateBidStatus = async (id: string, status: BidStatus) => {
  try {
    const { data } = await athleteApi.patch(`/athlete/collaborations/${id}`, { status })
    return data
  } catch (error) {
    throw new Error(`Failed to update bid status: ${error.message}`)
  }
}

// Update a collaboration (e.g., submit live post link)
export const updateCollaboration = async (
  campaignId: string,
  updates: {
    livePostLink?: string
    notificationShown?: boolean
    dueDateNotificationShown?: boolean
  },
) => {
  const { data } = await athleteApi.patch(`/athlete/collaborations/${campaignId}`, updates)
  return data
}

// Instagram OAuth API functions
export const initiateInstagramConnection = async () => {
  // This will redirect to Instagram's authorization page, so we just need to send the browser there
  window.location.href = '/api/athlete/oauth/instagram-login'
}

export const handleInstagramCallback = async (code: string, state: string) => {
  try {
    const { data } = await athleteApi.post('/athlete/oauth/instagram/callback', { code, state })
    return data as {
      success: boolean
      username: string
      followers: number | null
      missing_permissions: string[]
    }
  } catch (error) {
    throw new Error(
      error?.response?.data?.error || `Failed to complete Instagram connection: ${error.message}`,
    )
  }
}

// TikTok OAuth API functions
// TikTok connects through Talent Pitch Pro's connect site, which owns the TikTok app. The
// backend signs a hand-off for the signed-in athlete so the connection comes back to Athios.
export const initiateTikTokConnection = async () => {
  const { data } = await athleteApi.get('/athlete/oauth/tiktok/tpp-handoff')
  window.location.href = data.url
}

export const handleTikTokCallback = async (callbackData: {
  code: string
  scopes?: string
  state?: string
  social_account_id?: string
}) => {
  try {
    const { data } = await athleteApi.post('/athlete/oauth/auth/tiktok/callback', callbackData)
    return data
  } catch (error) {
    throw new Error(`Failed to complete TikTok connection: ${error.message}`)
  }
}

export const confirmTikTokInfo = async (
  params: { social_account_id?: string; confirm?: boolean } = {},
) => {
  try {
    const payload: { social_account_id?: string; confirm?: boolean } = {
      confirm: params.confirm ?? true,
    }

    if (params.social_account_id) {
      payload.social_account_id = params.social_account_id
    }

    const { data } = await athleteApi.post('/athlete/oauth/tiktok/confirm-info', payload)
    return data
  } catch (error) {
    throw new Error(`Failed to confirm TikTok account info: ${error.message}`)
  }
}

// YouTube OAuth API functions
export const initiateYouTubeConnection = async () => {
  // This will redirect to Google OAuth for YouTube
  window.location.href = '/api/athlete/oauth/youtube-login'
}

export const handleYouTubeCallback = async (callbackData: {
  code: string
  state?: string
  social_account_id?: string
}) => {
  try {
    const { data } = await athleteApi.post('/athlete/oauth/auth/youtube/callback', callbackData)
    return data
  } catch (error) {
    throw new Error(`Failed to complete YouTube connection: ${error.message}`)
  }
}

export const fetchYouTubeChannels = async (social_account_id?: string) => {
  try {
    const { data } = await athleteApi.post('/athlete/oauth/youtube/channels', {
      social_account_id,
    })
    return data
  } catch (error) {
    throw new Error(`Failed to fetch YouTube channels: ${error.message}`)
  }
}

export const confirmYouTubeChannel = async (channelData: {
  channel_id: string
  social_account_id?: string
  token_id: string
}) => {
  try {
    const { data } = await athleteApi.post('/athlete/oauth/confirm-youtube-channel', channelData)
    return data
  } catch (error) {
    throw new Error(`Failed to confirm YouTube channel: ${error.message}`)
  }
}

// Google Calendar OAuth API functions
export const initiateGoogleCalendarConnection = async () => {
  try {
    const { data } = await athleteApi.get('/athlete/oauth/google-calendar-login')

    if (!data?.redirect_url) {
      throw new Error('Backend did not return an OAuth redirect URL')
    }

    window.location.href = data.redirect_url
    return data
  } catch (error) {
    throw new Error(`Failed to initiate Google Calendar connection: ${error.message}`)
  }
}

export const handleGoogleCalendarCallback = async (callbackData: {
  code: string
  state?: string
}) => {
  try {
    const { data } = await athleteApi.post('/athlete/oauth/google-calendar/callback', callbackData)
    return data
  } catch (error) {
    throw new Error(`Failed to complete Google Calendar connection: ${error.message}`)
  }
}

export const fetchMilestoneEvents = async (params: { start: string; end: string }) => {
  try {
    const { data } = await athleteApi.get('/athlete/calendar/milestone-events', { params })
    return data
  } catch (error) {
    throw new Error(`Failed to fetch milestone events: ${error.message}`)
  }
}

export const fetchGoogleCalendarEvents = async (params: { start: string; end: string }) => {
  try {
    const { data } = await athleteApi.get('/athlete/calendar/events', {
      params,
    })
    return data
  } catch (error) {
    throw new Error(`Failed to fetch Google Calendar events: ${error.message}`)
  }
}

export const fetchGoogleCalendars = async () => {
  try {
    const { data } = await athleteApi.get('/athlete/calendar/calendars')
    return data
  } catch (error) {
    throw new Error(`Failed to fetch Google calendars: ${error.message}`)
  }
}

export const saveSelectedGoogleCalendars = async (calendarIds: string[]) => {
  try {
    const { data } = await athleteApi.patch('/athlete/calendar/calendars', {
      calendarIds,
    })
    return data
  } catch (error) {
    throw new Error(`Failed to save Google calendar selection: ${error.message}`)
  }
}

// Outlook Calendar OAuth API functions
export const initiateOutlookCalendarConnection = async () => {
  try {
    const { data } = await athleteApi.get('/athlete/oauth/outlook-calendar-login')

    if (!data?.redirect_url) {
      throw new Error('Backend did not return an OAuth redirect URL')
    }

    window.location.href = data.redirect_url
    return data
  } catch (error) {
    throw new Error(`Failed to initiate Outlook Calendar connection: ${error.message}`)
  }
}

export const handleOutlookCalendarCallback = async (callbackData: {
  code: string
  state?: string
}) => {
  try {
    const { data } = await athleteApi.post('/athlete/oauth/outlook-calendar/callback', {
      ...callbackData,
    })

    return data
  } catch (error) {
    throw new Error(`Failed to complete Outlook Calendar connection: ${error.message}`)
  }
}

export const fetchOutlookCalendarEvents = async (params: { start: string; end: string }) => {
  try {
    const { data } = await athleteApi.get('/athlete/calendar/outlook/events', {
      params,
    })
    return data
  } catch (error) {
    throw new Error(`Failed to fetch Outlook Calendar events: ${error.message}`)
  }
}

// iCal Feed API functions
export const generateICalToken = async () => {
  try {
    const { data } = await athleteApi.post('/athlete/calendar/ical/token')
    return data
  } catch (error) {
    throw new Error(`Failed to generate iCal token: ${error.message}`)
  }
}

export const getICalToken = async () => {
  try {
    const { data } = await athleteApi.get('/athlete/calendar/ical/token')
    return data
  } catch (error) {
    throw new Error(`Failed to fetch iCal token: ${error.message}`)
  }
}

export const revokeICalToken = async () => {
  try {
    const { data } = await athleteApi.delete('/athlete/calendar/ical/token')
    return data
  } catch (error) {
    throw new Error(`Failed to revoke iCal token: ${error.message}`)
  }
}

// Search for schools
export const searchSchools = async (query: string) => {
  try {
    const { data } = await athleteApi.get(`/system/school-search?q=${encodeURIComponent(query)}`)
    return data.schools
  } catch (error) {
    throw new Error(`Failed to search schools: ${error.message}`)
  }
}
